// The session's tokens (P1-E05; ARCHITECTURE §2):
// - the access token lives in memory only (never localStorage);
// - the refresh token is an HttpOnly SameSite=Strict cookie on path /admin/auth, so POST /admin/auth/refresh
//   must be same-origin with credentials (the dev server proxies /admin to the API);
// - a 401 `token_expired` refreshes once, silently, and replays the request; so does a 401 on a page load
//   (no token in memory yet, the cookie may still be valid).
// Raw fetch here, not the typed client, so refreshing never recurses through itself.
import { env } from '@/app/env';
import type { components } from './schema.gen';

export type TokenOut = components['schemas']['TokenOut'];

let accessToken: string | null = null;
/** The second sign-in step's token (an enrolled Admin): kept until /mfa/verify uses it. */
let mfaToken: string | null = null;

export type AuthProblem = 'signed_out' | 'mfa_required';
const problemListeners = new Set<(p: AuthProblem) => void>();

/** Subscribe to "the session ended" and "MFA is still required" (the shell redirects). */
export function onAuthProblem(listener: (p: AuthProblem) => void): () => void {
  problemListeners.add(listener);
  return () => problemListeners.delete(listener);
}
function report(p: AuthProblem) {
  problemListeners.forEach((l) => l(p));
}

export function getAccessToken(): string | null {
  return accessToken;
}
export function getMfaToken(): string | null {
  return mfaToken;
}

/** Stores what a sign-in step returned. */
export function applyTokens(t: TokenOut): void {
  if (t.access_token) accessToken = t.access_token;
  mfaToken = t.mfa_required ? (t.mfa_token ?? null) : null;
}

export function clearTokens(): void {
  accessToken = null;
  mfaToken = null;
}

export function url(path: string): string {
  return `${env.apiBaseUrl}${path}`;
}

let refreshing: Promise<boolean> | null = null;

/** POST /admin/auth/refresh with the cookie; single-flight (parallel 401s share one refresh). */
export function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await globalThis.fetch(url('/admin/auth/refresh'), { method: 'POST', credentials: 'include' });
        if (!res.ok) {
          accessToken = null;
          return false;
        }
        const body = (await res.json()) as TokenOut;
        if (!body.access_token) return false;
        accessToken = body.access_token;
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

async function codeOf(res: Response): Promise<string | null> {
  try {
    const body = (await res.clone().json()) as { code?: unknown };
    return typeof body.code === 'string' ? body.code : null;
  } catch {
    return null;
  }
}

const NO_REFRESH = ['/admin/auth/login', '/admin/auth/refresh', '/admin/auth/logout', '/admin/auth/mfa/verify'];

function withToken(req: Request): Request {
  if (accessToken && !req.headers.has('Authorization')) req.headers.set('Authorization', `Bearer ${accessToken}`);
  return req;
}

/** The typed client's fetch: bearer token, a silent refresh on 401, and session problems reported. */
export async function authFetch(input: Request): Promise<Response> {
  const retry = input.clone();
  const hadToken = !!accessToken;
  // Resolved per call, so interceptors installed later (MSW in tests) see every request.
  const res = await globalThis.fetch(withToken(input));
  const path = new URL(input.url, 'http://x').pathname;
  if (res.status === 401 && !NO_REFRESH.includes(path)) {
    const code = await codeOf(res);
    if (code === 'token_expired' || (!hadToken && code === 'not_authenticated')) {
      if (await refreshSession()) return globalThis.fetch(withToken(retry));
    }
    if (path !== '/admin/auth/me') report('signed_out');
    return res;
  }
  if (res.status === 403 && (await codeOf(res)) === 'mfa_required') report('mfa_required');
  return res;
}
